import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { AgEditorComponent } from '../../../../global/gx-ag-grid/ag-editor.component';
import { Location } from '../../../../../models/locations-models/location';
import { FeedbackService } from '../../../../../core/feedback.service';
import { ICellEditorParams } from 'ag-grid-community';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SocialMediaUrlsTControl } from '../../../../../models/locations-models/edit-location-form';
import { SocialMediaUrl, SocialMediaUrls } from '../../../../../models/locations-models/social-media-urls';
import { getFullNameForFieldID } from '../../../location-utils';
import { copyContentToClipboard } from '../../../../../utils/miscellaneous';
import { GxInputDirective } from '../../../../../directives/gxinput.directive';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatInput } from '@angular/material/input';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatTabGroup, MatTab, MatTabLabel } from '@angular/material/tabs';
import { NgClass } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { LetDirective } from '@ngrx/component';

@Component({
    selector: 'app-social-media-links-editor',
    templateUrl: './social-media-links-editor.component.html',
    styleUrls: ['../simple-text-editor/simple-text-editor.component.scss',
                '../business-hours-editor/business-hours-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [LetDirective, CdkTrapFocus, NgClass, MatTabGroup, MatTab, MatTabLabel, MatIcon, FormsModule, ReactiveFormsModule, MatFormField, MatInput, CdkTextareaAutosize, GxInputDirective]
})
export class SocialMediaLinksEditorComponent extends AgEditorComponent<Location> {
  protected readonly SocialMediaUrl = SocialMediaUrl;
  protected readonly getFullNameForFieldID = getFullNameForFieldID;
  protected readonly media: string[] = ['facebook', 'instagram', 'linkedin', 'pinterest', 'tiktok', 'twitter', 'youtube'];

  @ViewChild('textInput') textInput: ElementRef;

  constructor(protected feedbackService: FeedbackService,
              protected cd: ChangeDetectorRef,
              private fb: FormBuilder) {
    super(feedbackService, cd);
  }

  protected override createEditorFormGroup(params: ICellEditorParams<Location>): FormGroup<SocialMediaUrlsTControl> {
    return this.fb.group(
        {
          [SocialMediaUrl.FACEBOOK]: [params?.data?.socialMediaUrls?.url_facebook ? params.data.socialMediaUrls?.url_facebook : ''],
          [SocialMediaUrl.INSTAGRAM]: [params?.data?.socialMediaUrls?.url_instagram ? params.data.socialMediaUrls.url_instagram : ''],
          [SocialMediaUrl.LINKEDIN]: [params?.data?.socialMediaUrls?.url_linkedin ? params.data.socialMediaUrls.url_linkedin : ''],
          [SocialMediaUrl.PINTEREST]: [params?.data?.socialMediaUrls?.url_pinterest ? params.data.socialMediaUrls.url_pinterest : ''],
          [SocialMediaUrl.TIKTOK]: [params?.data?.socialMediaUrls?.url_tiktok ? params.data.socialMediaUrls.url_tiktok : ''],
          [SocialMediaUrl.TWITTER]: [params?.data?.socialMediaUrls?.url_twitter ? params.data.socialMediaUrls.url_twitter : ''],
          [SocialMediaUrl.YOUTUBE]: [params?.data?.socialMediaUrls?.url_youtube ? params.data.socialMediaUrls.url_youtube : ''],
        },
    );
  }

  /**
   * Return the new location field value
   * Override in child component if needed
   * @protected
   */
  protected override getNewFieldValue(): SocialMediaUrls {
    const urls = {} as SocialMediaUrls;

    if (this.editorFormGroup.get(SocialMediaUrl.FACEBOOK).value) {
      urls.url_facebook = this.editorFormGroup.get(SocialMediaUrl.FACEBOOK).value;
    }

    if (this.editorFormGroup.get(SocialMediaUrl.INSTAGRAM).value) {
      urls.url_instagram = this.editorFormGroup.get(SocialMediaUrl.INSTAGRAM).value;
    }

    if (this.editorFormGroup.get(SocialMediaUrl.LINKEDIN).value) {
      urls.url_linkedin = this.editorFormGroup.get(SocialMediaUrl.LINKEDIN).value;
    }

    if (this.editorFormGroup.get(SocialMediaUrl.PINTEREST).value) {
      urls.url_pinterest = this.editorFormGroup.get(SocialMediaUrl.PINTEREST).value;
    }

    if (this.editorFormGroup.get(SocialMediaUrl.TIKTOK).value) {
      urls.url_tiktok = this.editorFormGroup.get(SocialMediaUrl.TIKTOK).value;
    }

    if (this.editorFormGroup.get(SocialMediaUrl.TWITTER).value) {
      urls.url_twitter = this.editorFormGroup.get(SocialMediaUrl.TWITTER).value;
    }

    if (this.editorFormGroup.get(SocialMediaUrl.YOUTUBE).value) {
      urls.url_youtube = this.editorFormGroup.get(SocialMediaUrl.YOUTUBE).value;
    }

    return Object.keys(urls).length ? urls : null;
  };

  /**
   * A hook to perform any necessary operation just after the GUI for this component has been
   * rendered on the screen.
   * This method is called each time the edit component is activated.
   * This is useful for any logic that requires attachment before executing, such as putting focus
   * on a particular DOM element.
   */
  afterGuiAttached(): void {
    this.textInput?.nativeElement?.focus();
  }

  /**
   * Copy text input to clipboard.
   * @param input Textarea / Input reference
   */
  copyTextInput(input: HTMLTextAreaElement | HTMLInputElement) {
    copyContentToClipboard(input, (ok) => {
      ok && this.feedbackService.showSuccessSnackbar({
        data: { userFriendlyMessage: 'Copied' },
        isAddressListSnackbar: true,
      });
    });
  }
}
